import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import Swal from 'sweetalert2'
import {GET, PUT} from '../../../services/api'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {i18n} from '../../../translate/i18n'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {AgeGroup, getAgeGroupLabel} from '../Activities/types'

// Types
export enum ScheduleType {
  ENTERTAINMENT = 'entertainment',
  DINING = 'dining',
  SPORTS = 'sports',
  WELLNESS = 'wellness',
  EVENT = 'event',
  TOUR = 'tour',
  OTHER = 'other',
}

interface UpdateHotelScheduleDto {
  title?: string
  description?: string
  location?: string
  type?: ScheduleType
  ageGroup?: AgeGroup
  time?: string
  days?: string
  dateInit?: string
  dateEnd?: string
  status?: boolean
}

interface HotelSchedule {
  id: number
  pluginspaceId: number
  title: string
  description: string
  location: string
  type: ScheduleType
  ageGroup: AgeGroup
  time: string
  days?: string
  dateInit?: string
  dateEnd?: string
  status: boolean
  createdAt: string
  updatedAt: string
}

enum RecurrenceType {
  periodic = 'periodic',
  daysOfWeek = 'daysOfWeek',
  dayWithinDateRange = 'dayWithinDateRange',
}

const ScheduleEditContent: FC = () => {
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [originalData, setOriginalData] = useState<HotelSchedule | null>(null)
  const [formData, setFormData] = useState<UpdateHotelScheduleDto>({})
  const [selectedDays, setSelectedDays] = useState<number[]>([])
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(RecurrenceType.periodic)
  const getSchedule = async () => {
    if (!id) return

    try {
      setLoadingData(true)
      const res = await GET(`/hotel-schedule/${id}`)
      const schedule: HotelSchedule = res.data

      setOriginalData(schedule)
      setFormData({
        title: schedule.title,
        description: schedule.description,
        location: schedule.location,
        type: schedule.type,
        time: schedule.time,
        ageGroup: schedule.ageGroup,
        days: schedule.days || '',
        dateInit: schedule.dateInit || '',
        dateEnd: schedule.dateEnd || '',
        status: schedule.status,
      })

      // Configurar tipo de evento
      const hasRecurring = Boolean(schedule.days)
      const hasDate = Boolean(schedule.dateInit || schedule.dateEnd)

      if (hasRecurring && hasDate) {
        setRecurrenceType(RecurrenceType.dayWithinDateRange)
      } else if (hasRecurring) {
        setRecurrenceType(RecurrenceType.daysOfWeek)
      } else {
        setRecurrenceType(RecurrenceType.periodic)
      }

      // Configurar dias selecionados se for recorrente
      if (hasRecurring && schedule.days) {
        const days = schedule.days
          .split('')
          .map((day) => parseInt(day))
          .filter((day) => !isNaN(day))
        setSelectedDays(days)
      }

      setLoadingData(false)
    } catch (error) {
      setLoadingData(false)
      console.error(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao carregar dados da programação!',
      }).then(() => {
        navigate('/hospitality/hotel-schedules')
      })
    }
  }

  const handleInputChange = (field: keyof UpdateHotelScheduleDto, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleDayToggle = (dayIndex: number) => {
    const newSelectedDays = selectedDays.includes(dayIndex)
      ? selectedDays.filter((day) => day !== dayIndex)
      : [...selectedDays, dayIndex].sort()

    setSelectedDays(newSelectedDays)
    handleInputChange('days', newSelectedDays.join(','))
  }

  function hasDays() {
    return (
      recurrenceType === RecurrenceType.daysOfWeek ||
      recurrenceType === RecurrenceType.dayWithinDateRange
    )
  }

  function hasDates() {
    return (
      recurrenceType === RecurrenceType.periodic ||
      recurrenceType === RecurrenceType.dayWithinDateRange
    )
  }

  const handleSubmit = async () => {
    if (!id || !originalData) return

    try {
      setLoading(true)

      // Validações
      if (!formData.title?.trim()) {
        throw new Error('Título é obrigatório')
      }
      if (!formData.description?.trim()) {
        throw new Error('Descrição é obrigatória')
      }
      if (!formData.location?.trim()) {
        throw new Error('Local é obrigatório')
      }
      if (!formData.time?.trim()) {
        throw new Error('Horário é obrigatório')
      }
      // Validação do formato do horário
      const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)(\s*-\s*([01]\d|2[0-3]):([0-5]\d))?$/
      if (!timeRegex.test(formData.time.trim())) {
        throw new Error('Horário deve estar no formato "HH:MM" ou "HH:MM - HH:MM"')
      }

      const daysFormated = formData.days
        ? formData.days
            .split(',')
            .map((day) => day.trim())
            .join('')
        : ''

      // Preparar dados para envio
      const dataToSend = {
        ...formData,
        days: hasDays() ? daysFormated : '',
        dateInit: hasDates() ? formData.dateInit : undefined,
        dateEnd: hasDates() ? formData.dateEnd : undefined,
      }

      // Remove campos vazios opcionais
      Object.keys(dataToSend).forEach((key) => {
        if (dataToSend[key as keyof typeof dataToSend] === '') {
          delete dataToSend[key as keyof typeof dataToSend]
        }
      })

      await PUT(`/hotel-schedule/${id}`, dataToSend)

      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Programação atualizada com sucesso!',
      })

      navigate('/hospitality/hotel-schedules')
    } catch (error: any) {
      setLoading(false)
      console.error(error)

      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error.message || 'Erro ao atualizar programação!',
      })
    }
  }

  useEffect(() => {
    getSchedule()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const dayNames = [
    i18n.t('shifts.sunday'),
    i18n.t('shifts.monday'),
    i18n.t('shifts.tuesday'),
    i18n.t('shifts.wednesday'),
    i18n.t('shifts.thursday'),
    i18n.t('shifts.friday'),
    i18n.t('shifts.saturday'),
  ]

  if (loadingData) {
    return <Loading />
  }

  if (!originalData) {
    return (
      <div className='alert alert-danger'>
        <h4>{i18n.t('hospitality_schedule_edit.not_found.title')}</h4>
        <p>{i18n.t('hospitality_schedule_edit.not_found.description')}</p>
        <Button
          text={i18n.t('hospitality_schedule_edit.not_found.back_button')}
          color='primary'
          size='small'
          click={() => navigate('/hospitality/hotel-schedules')}
        />
      </div>
    )
  }

  const clearlySelectedDays = () => {
    setSelectedDays([])
    handleInputChange('days', '')
  }

  return (
    <>
      {loading && <Loading />}

      <div className='row g-5 gx-xxl-12'>
        <div className='card card-xxl-stretch mb-5 mb-xl-12'>
          <div className='card-header py-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bolder fs-3 mb-1'>
                {i18n.t('hospitality_schedule_edit.page_title')}
              </span>
              <BreadCrumbs />
              <span className='text-muted mt-1 fw-bold fs-7'>
                {i18n.t('hospitality_schedule_edit.page_subtitle', {title: originalData.title})}
              </span>
            </h3>
          </div>

          <div className='card-body'>
            <div className='row g-3'>
              <div className='col-md-4'>
                <Input
                  label={i18n.t('hospitality_schedule_create.form.title_label')}
                  inputAttr={{
                    type: 'text',
                    placeholder: i18n.t('hospitality_schedule_create.form.title_placeholder'),
                    maxLength: 200,
                  }}
                  value={formData.title}
                  change={(value: string) => handleInputChange('title', value)}
                  required
                />
              </div>

              <div className='col-md-4'>
                <Input
                  label={i18n.t('hospitality_schedule_create.form.type_label')}
                  inputAttr={{
                    type: 'select',
                    placeholder: i18n.t('hospitality_schedule_create.form.type_placeholder'),
                  }}
                  value={formData.type}
                  change={(value: ScheduleType) => handleInputChange('type', value)}
                  options={[
                    {
                      value: ScheduleType.ENTERTAINMENT,
                      select: i18n.t('hospitality_schedules.types.activity'),
                    },
                    {
                      value: ScheduleType.DINING,
                      select: i18n.t('hospitality_schedules.types.food'),
                    },
                    {
                      value: ScheduleType.SPORTS,
                      select: i18n.t('hospitality_schedule_create.form.type_sports'),
                    },
                    {
                      value: ScheduleType.WELLNESS,
                      select: i18n.t('hospitality_schedule_create.form.type_wellness'),
                    },
                    {
                      value: ScheduleType.EVENT,
                      select: i18n.t('hospitality_schedules.types.event'),
                    },
                    {value: ScheduleType.TOUR, select: i18n.t('hospitality_schedules.types.tour')},
                    {
                      value: ScheduleType.OTHER,
                      select: i18n.t('hospitality_schedule_create.form.type_other'),
                    },
                  ]}
                  required
                />
              </div>

              <div className='col-md-4'>
                <Input
                  inputAttr={{
                    type: 'select',
                  }}
                  label={i18n.t('hospitality_activities.field_age_group')}
                  options={Object.values(AgeGroup).map((ageGroup) => ({
                    value: ageGroup,
                    select: getAgeGroupLabel(ageGroup),
                  }))}
                  value={formData.ageGroup}
                  change={(value: AgeGroup) => handleInputChange('ageGroup', value)}
                />
              </div>
              <div className='col-12'>
                <EditorQuill
                  value={formData.description}
                  label={i18n.t('hospitality_schedule_create.form.description_label')}
                  placeholder={i18n.t('hospitality_schedule_create.form.description_placeholder')}
                  change={(value: string) => handleInputChange('description', value)}
                  maxLength={5000} // Limite de 5000 caracteres
                />
              </div>
              <div className='col-md-6'>
                <Input
                  label={i18n.t('hospitality_schedule_edit.form.location_label')}
                  inputAttr={{
                    type: 'text',
                    placeholder: i18n.t('hospitality_schedule_edit.form.location_placeholder'),
                    maxLength: 100,
                  }}
                  value={formData.location || ''}
                  change={(value: string) => handleInputChange('location', value)}
                  required
                />
              </div>

              <div className='col-md-6'>
                <Input
                  label={i18n.t('hospitality_schedule_edit.form.time_label')}
                  inputAttr={{
                    type: 'text',
                    placeholder: i18n.t('hospitality_schedule_edit.form.time_placeholder'),
                    maxLength: 50,
                  }}
                  value={formData.time || ''}
                  change={(value: string) => handleInputChange('time', value)}
                  smalltext={i18n.t('hospitality_schedule_edit.form.time_smalltext')}
                  required
                />
              </div>

              {/* Tipo de Evento */}
              <div className='col-12'>
                <div className='separator separator-dashed my-5'></div>
                <h5 className='mb-4'>
                  {i18n.t('hospitality_schedule_edit.form.recurrence_config')}
                </h5>

                <div className='row'>
                  <div className='col-md-12'>
                    <Input
                      inputAttr={{
                        type: 'radio',
                      }}
                      label={i18n.t('hospitality_schedule_create.form.single_event_radio')}
                      checked={recurrenceType === RecurrenceType.periodic}
                      change={(checked: boolean) => {
                        if (checked) {
                          setRecurrenceType(RecurrenceType.periodic)
                          clearlySelectedDays()
                        }
                      }}
                      name='eventType'
                    />
                  </div>
                  <div className='col-md-12'>
                    <Input
                      inputAttr={{
                        type: 'radio',
                      }}
                      label={i18n.t('hospitality_schedule_create.form.recurring_event_radio')}
                      checked={recurrenceType === RecurrenceType.daysOfWeek}
                      change={(checked: boolean) => {
                        if (checked) {
                          setRecurrenceType(RecurrenceType.daysOfWeek)
                          handleInputChange('dateInit', '')
                          handleInputChange('dateEnd', '')
                        }
                      }}
                      name='eventType'
                    />
                  </div>
                  <div className='col-md-12'>
                    <Input
                      inputAttr={{
                        type: 'radio',
                      }}
                      label={i18n.t('hospitality_schedule_create.form.day_within_date_range_radio')}
                      checked={recurrenceType === RecurrenceType.dayWithinDateRange}
                      change={(checked: boolean) => {
                        if (checked) {
                          setRecurrenceType(RecurrenceType.dayWithinDateRange)
                          handleInputChange('dateInit', '')
                          handleInputChange('dateEnd', '')
                        }
                      }}
                      name='eventType'
                    />
                  </div>
                </div>
              </div>

              {/* Dias da Semana - Evento Recorrente */}
              {(recurrenceType === RecurrenceType.daysOfWeek ||
                recurrenceType === RecurrenceType.dayWithinDateRange) && (
                <div className='col-12'>
                  <label className='form-label fw-bold'>
                    {i18n.t('hospitality_schedule_edit.form.days_of_week_label')}
                  </label>
                  <div className='d-flex flex-wrap gap-3 mt-3'>
                    {dayNames.map((day, index) => (
                      <Input
                        key={index}
                        inputAttr={{
                          type: 'checkbox',
                        }}
                        label={day}
                        checked={selectedDays.includes(index)}
                        change={(checked: boolean) => {
                          if (checked) {
                            handleDayToggle(index)
                          } else {
                            handleDayToggle(index)
                          }
                        }}
                      />
                    ))}
                  </div>
                  {selectedDays.length > 0 && (
                    <div className='text-muted mt-2'>
                      {i18n.t('hospitality_schedule_edit.form.selected_days', {
                        days: selectedDays.map((day) => dayNames[day]).join(', '),
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Datas - Evento Único */}
              {(recurrenceType === RecurrenceType.periodic ||
                recurrenceType === RecurrenceType.dayWithinDateRange) && (
                <>
                  <div className='col-md-6'>
                    <Input
                      label={i18n.t('hospitality_schedule_edit.form.date_init_label')}
                      inputAttr={{
                        type: 'date',
                        placeholder: i18n.t('hospitality_schedule_edit.form.date_init_placeholder'),
                      }}
                      value={formData.dateInit || ''}
                      change={(value: string) => handleInputChange('dateInit', value)}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      label={i18n.t('hospitality_schedule_edit.form.date_end_label')}
                      inputAttr={{
                        type: 'date',
                        placeholder: i18n.t('hospitality_schedule_edit.form.date_end_placeholder'),
                      }}
                      value={formData.dateEnd || ''}
                      change={(value: string) => handleInputChange('dateEnd', value)}
                    />
                  </div>
                </>
              )}

              {/* Status */}
              <div className='col-12'>
                <div className='separator separator-dashed my-5'></div>
                <Input
                  label={i18n.t('hospitality_schedule_edit.form.status_label')}
                  inputAttr={{
                    type: 'checkbox',
                  }}
                  checked={formData.status}
                  change={(checked: boolean) => handleInputChange('status', checked)}
                  checkSwift
                />
              </div>
            </div>
            <div className='d-flex justify-content-end'>
              <Button
                text={i18n.t('hospitality_schedule_edit.form.back_button')}
                color='danger'
                size='small'
                click={() => navigate('/hospitality/hotel-schedules')}
                btnClass='me-3'
              />
              <Button
                text={i18n.t('hospitality_schedule_edit.form.save_button')}
                color='primary'
                size='small'
                click={handleSubmit}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const ScheduleEdit: FC = () => {
  const {id} = useParams<{id: string}>()

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: i18n.t('hospitality_schedules.page_title'), path: 'hospitality', isActive: false},
          {
            title: i18n.t('hospitality_schedules.page_title'),
            path: 'hospitality/schedules',
            isActive: false,
          },
          {
            title: i18n.t('hospitality_schedule_edit.page_title'),
            path: `hospitality/schedules/edit/${id}`,
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_schedule_edit.page_title')}
      </PageTitle>
      <ScheduleEditContent />
    </>
  )
}

export {ScheduleEdit}
